import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SeoArticleSchema from "@/components/SeoArticleSchema";
import { ContextualLinkPanel } from "@/components/ContextualLinkPanel";
import { InsightKeyTakeaway } from "@/components/InsightKeyTakeaway";
import PortugalSpainQuickFitCheck from "@/components/PortugalSpainQuickFitCheck";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const sections = [
  {
    title: "Why are healthcare rankings alone not enough?",
    paragraphs: [
      "A strong national healthcare reputation can still feel different on the ground depending on where you live, how quickly you need care, and whether you expect public or private options.",
      "That is why healthcare confidence should be treated as part of overall country fit, not as a standalone trophy metric.",
    ],
  },
  {
    title: "What do expats usually care about most in healthcare?",
    bullets: [
      "quality and consistency of care",
      "ease of access and waiting times",
      "how comfortable the system feels in day-to-day use",
      "whether the broader country still fits your budget and life outside the clinic",
    ],
  },
  {
    title: "How should healthcare change the shortlist?",
    paragraphs: [
      "Countries like Spain, France, Portugal, Germany, and others often stay strong because healthcare confidence reinforces the rest of the move. But healthcare rarely decides the answer alone. It has to hold together with affordability, residency practicality, and daily life.",
    ],
  },
  {
    title: "What should you compare before calling one country stronger?",
    bullets: [
      "whether the healthcare advantage is meaningful for your stage of life",
      "how much system variation you would actually tolerate",
      "whether the country still makes sense once healthcare is weighed beside the other tradeoffs",
    ],
  },
];

const HEALTHCARE_QUICK_FIT_QUESTIONS = [
  {
    id: "quality_vs_cost",
    question: "What matters more in your healthcare decision?",
    options: [
      { label: "The strongest overall healthcare quality", scores: { ES: 2, FR: 2, DE: 1 } },
      { label: "Keeping healthcare costs lower and more manageable", scores: { PT: 2, MX: 1, ES: 1 } },
    ],
  },
  {
    id: "specialist_access",
    question: "How important is fast access to specialists?",
    options: [
      { label: "Very important", scores: { FR: 2, DE: 2, ES: 1 } },
      { label: "Important, but overall affordability still matters", scores: { PT: 2, ES: 1, MX: 1 } },
    ],
  },
  {
    id: "english_support",
    question: "How important are English-speaking providers?",
    options: [
      { label: "I want easier English-speaking access", scores: { PT: 2, MX: 1 } },
      { label: "I can adapt if the healthcare quality is strong", scores: { ES: 2, FR: 1, DE: 1 } },
    ],
  },
  {
    id: "public_private_preference",
    question: "What care model sounds more comfortable?",
    options: [
      { label: "A stronger public system with broad coverage", scores: { ES: 2, FR: 2, DE: 1 } },
      { label: "A balanced public-private mix with more flexibility", scores: { PT: 2, MX: 1, DE: 1 } },
    ],
  },
  {
    id: "expense_predictability",
    question: "What kind of healthcare spending feels better to you?",
    options: [
      { label: "Lower and more predictable monthly cost pressure", scores: { PT: 2, ES: 1, MX: 1 } },
      { label: "Higher confidence even if the system is more complex", scores: { FR: 2, DE: 2, ES: 1 } },
    ],
  },
];

const HEALTHCARE_COUNTRY_LABELS = {
  PT: "Portugal",
  ES: "Spain",
  FR: "France",
  DE: "Germany",
  MX: "Mexico",
};

const HEALTHCARE_COUNTRY_EXPLANATIONS = {
  PT: "Portugal often rises when you want a strong healthcare experience that still feels more manageable on cost, especially if a balanced public-private approach sounds workable.",
  ES: "Spain often rises when healthcare confidence, public-system depth, and dependable day-to-day access carry more weight than pure cost minimization.",
  FR: "France often rises when overall system quality, specialist access, and comprehensive care structure matter enough to justify more complexity or cost.",
  DE: "Germany often rises when predictability, specialist access, and system depth matter more than keeping healthcare spending as low as possible.",
  MX: "Mexico often rises when cost awareness and easier access to private care matter more than maximizing national-system prestige.",
};

const HEALTHCARE_FAQ_ITEMS = [
  {
    question: "Should I choose a country based on healthcare alone?",
    answer:
      "Usually no. Healthcare can be the leading filter, but it still has to hold up beside affordability, residency practicality, language comfort, and the rest of your daily life abroad.",
  },
  {
    question: "Why do countries with great healthcare still feel different for expats?",
    answer:
      "Because healthcare quality on paper is only part of the experience. Expats also feel the difference through appointment access, language ease, system navigation, private-care options, and how the rest of life works around the care itself.",
  },
  {
    question: "Does lower healthcare cost always mean weaker care?",
    answer:
      "Not necessarily. Some countries offer a strong value balance, especially if you are comfortable with a mixed public-private approach. The real question is whether the care feels strong enough for your needs without undermining the rest of the move.",
  },
  {
    question: "What should I do if more than one healthcare-oriented country still looks strong?",
    answer:
      "That usually means your decision needs more than one-factor screening. The full assessment helps weigh healthcare beside cost, residency, pace of life, and other practical realities that can change the final lead.",
  },
];

const buildHealthcareQuickFitResult = ({ lead, closeAlternativeNames = [] }) => ({
  headline: `${lead.name} may deserve the first closer look for healthcare fit`,
  body: HEALTHCARE_COUNTRY_EXPLANATIONS[lead.code] || `${lead.name} appears to align most closely with the healthcare priorities you selected.`,
  supportingNote: closeAlternativeNames.length
    ? closeAlternativeNames.length === 1
      ? `${closeAlternativeNames[0]} also came through as a strong match from the healthcare priorities you selected. The full Relocation Clarity Assessment considers many additional factors that may still change the final recommendation.`
      : `${closeAlternativeNames.slice(0, -1).join(", ")} and ${closeAlternativeNames[closeAlternativeNames.length - 1]} also came through as strong matches from the healthcare priorities you selected. The full Relocation Clarity Assessment considers many additional factors that may still change the final recommendation.`
    : "This is a directional result, not a final recommendation. The full Relocation Clarity Assessment considers many additional factors that may still change the final recommendation.",
});

export default function CountriesWithBestHealthcareForExpats() {
  return (
    <div className="min-h-screen bg-[#f7f4ee] text-stone-900" data-testid="best-healthcare-expats-page">
      <SeoArticleSchema
        headline="Countries With the Best Healthcare for Expats"
        description="Compare countries for expat healthcare through quality, access, peace of mind, and how strongly healthcare should shape your relocation shortlist."
        pathname="/insights/countries-with-the-best-healthcare-for-expats"
        keywords={[
          "countries with the best healthcare for expats",
          "best healthcare abroad for expats",
          "healthcare for expats by country",
        ]}
      />
      <Header />
      <main className="px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <div className="mx-auto max-w-6xl space-y-8">
          <section className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-10 shadow-[0_18px_48px_rgba(28,25,23,0.06)] sm:px-10" data-testid="best-healthcare-expats-hero-section">
            <div className="mb-5 h-1.5 w-16 rounded-full bg-amber-400/80" />
            <p className="rc-section-kicker mb-4">Relocation insight</p>
            <h1 className="font-manrope text-4xl text-stone-900 sm:text-5xl">Countries With the Best Healthcare for Expats</h1>
            <div className="mt-6 max-w-4xl space-y-4 text-base leading-8 text-stone-600">
              <p>Healthcare quality matters differently depending on age, peace-of-mind needs, and how much system variability you are willing to accept in day-to-day life abroad.</p>
              <p>A stronger healthcare answer is useful only if the rest of the move still works around it.</p>
            </div>
          </section>

          <InsightKeyTakeaway
            testIdPrefix="best-healthcare-expats-key-takeaway"
            paragraphs={[
              "Countries with strong healthcare systems often stay near the top of expat shortlists, but healthcare alone is rarely enough to decide where you should live.",
              "A stronger healthcare answer becomes useful only when it still holds together with affordability, residency practicality, pace of life, and the kind of access and reassurance you personally need.",
            ]}
          />

          <PortugalSpainQuickFitCheck
            rootTestId="best-healthcare-expats-quick-fit"
            quickFitType="healthcare_expats"
            questions={HEALTHCARE_QUICK_FIT_QUESTIONS}
            countryLabels={HEALTHCARE_COUNTRY_LABELS}
            countryPriorityOrder={["ES", "FR", "PT", "DE", "MX"]}
            buildResult={buildHealthcareQuickFitResult}
          />

          <section className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-10" data-testid="best-healthcare-expats-faq-section">
            <div className="max-w-4xl">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500" data-testid="best-healthcare-expats-faq-kicker">Questions people often still have</p>
              <h2 className="mt-3 font-manrope text-3xl text-stone-900" data-testid="best-healthcare-expats-faq-title">Still Weighing Healthcare Abroad?</h2>
              <p className="mt-4 text-base leading-8 text-stone-600" data-testid="best-healthcare-expats-faq-description">
                These are the practical healthcare questions that often remain once one or two countries begin to look stronger on reassurance, access, or cost.
              </p>
            </div>

            <Accordion type="single" collapsible className="mt-7 space-y-3" data-testid="best-healthcare-expats-faq-accordion">
              {HEALTHCARE_FAQ_ITEMS.map((item, index) => (
                <AccordionItem key={item.question} value={`healthcare-faq-${index + 1}`} className="rounded-[1.4rem] border border-stone-200 bg-stone-50/75 px-5" data-testid={`best-healthcare-expats-faq-item-${index + 1}`}>
                  <AccordionTrigger className="py-5 text-left font-manrope text-lg text-stone-900 no-underline hover:no-underline" data-testid={`best-healthcare-expats-faq-trigger-${index + 1}`}>
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent>
                    <p className="pb-5 text-sm leading-7 text-stone-600" data-testid={`best-healthcare-expats-faq-answer-${index + 1}`}>
                      {item.answer}
                    </p>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          <section className="grid gap-6 lg:grid-cols-2" data-testid="best-healthcare-expats-sections-grid">
            {sections.map((section, index) => (
              <article key={section.title} className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-8" data-testid={`best-healthcare-expats-section-${index + 1}`}>
                <h2 className="font-manrope text-2xl text-stone-900">{section.title}</h2>
                <div className="mt-5 space-y-4 text-base leading-8 text-stone-600">
                  {section.paragraphs?.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
                </div>
                {section.bullets?.length ? (
                  <ul className="mt-5 space-y-3 text-sm leading-7 text-stone-600">
                    {section.bullets.map((bullet) => (
                      <li key={bullet} className="flex gap-3"><span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-amber-400/80" /><span>{bullet}</span></li>
                    ))}
                  </ul>
                ) : null}
              </article>
            ))}
          </section>

          <section className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-10" data-testid="best-healthcare-expats-followup-section">
            <h2 className="font-manrope text-2xl text-stone-900" data-testid="best-healthcare-expats-followup-title">How should you put healthcare back into the full decision?</h2>
            <div className="mt-5 max-w-4xl space-y-4 text-base leading-8 text-stone-600" data-testid="best-healthcare-expats-followup-copy">
              <p>
                If healthcare is your biggest concern, start by asking whether it is acting as a tie-breaker or the primary filter. That distinction matters because a country with slightly stronger healthcare may still be the weaker fit overall if budget or residency pressure becomes too high.
              </p>
              <p>
                This is also why readers often benefit from pairing this article with{" "}
                <Link to="/insights/what-country-should-i-move-to" className="font-medium text-stone-700 underline-offset-4 transition-colors duration-200 hover:text-stone-900 hover:underline" data-testid="best-healthcare-expats-what-country-link">
                  the broader country-fit guide
                </Link>
                {" "}or, if retirement is the context, with{" "}
                <Link to="/insights/portugal-vs-spain-for-american-retirees" className="font-medium text-stone-700 underline-offset-4 transition-colors duration-200 hover:text-stone-900 hover:underline" data-testid="best-healthcare-expats-retirees-link">
                  a more concrete retirement comparison
                </Link>
                .
              </p>
            </div>
          </section>

          <ContextualLinkPanel
            kicker="Continue exploring"
            title="If you are not ready for the full assessment yet, these are the most useful next pages from here."
            description="Healthcare matters most when it is weighed beside the other practical factors that shape life abroad. These pages help move from one strong factor into a fuller shortlist and a clearer view of the paid product."
            testIdPrefix="best-healthcare-expats-next-steps"
            items={[
              {
                slug: "fit-guide",
                eyebrow: "Reconnect the full decision",
                title: "See what actually determines country fit",
                description: "Put healthcare back beside affordability, residency practicality, and daily life before you decide too early.",
                ctaLabel: "Read the decision guide",
                to: "/insights/what-country-should-i-move-to",
              },
              {
                slug: "shortlist",
                eyebrow: "Narrow the field",
                title: "Build a realistic shortlist",
                description: "Use healthcare as one filter inside a smaller set of countries that still look workable overall.",
                ctaLabel: "Open the shortlist guide",
                to: "/insights/create-country-shortlist-for-moving-abroad",
              },
              {
                slug: "sample-blueprint",
                eyebrow: "See the paid view",
                title: "Preview a Sample Blueprint",
                description: "See how healthcare confidence appears inside a fuller recommendation rather than as a standalone ranking.",
                ctaLabel: "Preview the sample",
                to: "/sample-plan",
              },
            ]}
          />
        </div>
      </main>
      <Footer />
    </div>
  );
}